import React, { useEffect, useState } from 'react'
import OfferConfigurator from './offerConfigurator'
import { useQuery } from '@apollo/client'
import serverData from 'ppbl/utils/server-data'
import PageMessage from 'ppbl/components/PageMessage'
import { BodyText } from '@paypalcorp/pp-react'
import { getOffersByIdQuery } from 'ppbl/graphql/offers/queries'
import { useApplicationContext } from 'ppbl/hooks'
import clientLogger from 'ppbl/utils/client-logger'
import { getLocalizedText } from 'ppbl/utils/locale-helper'
import {
  determineSkipGetOffersCall,
  handleGraphqlError,
} from 'ppbl/screens/__utils__.js'
import { cloneAndMask } from 'ppbl/utils/data-masker'

type Props = {
  applicationType: string
}

/**
 * This is a functional component that displays offer page
 * it is resposible for calling offer API to get offer constraints to build the configurator
 * @returns {JSX.Element} - A JSX element
 */

const OfferPage: React.FC<Props> = ({ applicationType }) => {
  const { data, updateApplicationContext } = useApplicationContext()
  const appId = sessionStorage.getItem('appId') // will update with storage, when it is done

  const skipOffersQuery = determineSkipGetOffersCall(data)

  const {
    applicationMetaData: { applicationId },
    offerMetaData: {
      offerConstraints,
      averageMonthlyGrossSales,
      customOfferConstraints,
      productOfferConstraints,
    },
  } = data

  const renewalToken = serverData.startParams?.renewalToken

  const [customOfferConstraintsData, setCustomOfferConstraintsData] = useState(
    customOfferConstraints,
  )
  const [productOfferConstraintsData, setProductOfferConstraintsData] =
    useState(productOfferConstraints)

  const {
    loading: offersDataLoading,
    data: offersData,
    error: offersDataError,
  } = useQuery(getOffersByIdQuery, {
    variables: {
      applicationInput: { applicationId: appId },
    },
    skip: skipOffersQuery,
  })

  useEffect(() => {
    clientLogger.info(
      'application id',
      data?.applicationMetaData?.applicationId,
    )
    if (offersData && !offersDataLoading) {
      clientLogger.info(
        'Get Offers By ID Call successful - Response Received',
        cloneAndMask(offersData),
      )
      const {
        credit: {
          businessCredit: {
            offerConstraints: {
              customOfferConstraints: customOfferData,
              productOfferConstraints: productOfferData,
            },
          },
        },
      } = offersData

      setCustomOfferConstraintsData(customOfferData)
      setProductOfferConstraintsData(productOfferData)

      updateApplicationContext({
        applicationMetaData: {
          ...data?.applicationMetaData,
        },
        offerMetaData: {
          ...data?.offerMetaData,
          customOfferConstraints: customOfferData,
          productOfferConstraints: productOfferData,
        },
      })
    } else if (offersDataError) {
      handleGraphqlError(
        offersDataError,
        'Error encountered while fetching offerConstraints details in offer page',
        'OfferPage - getOffersByIdQuery',
        data?.applicationMetaData?.applicationId,
        renewalToken,
        data?.applicationMetaData?.legalBusinessName,
        applicationType,
      )
    }
  }, [offersDataLoading])

  return (
    <>
      {offersDataLoading ||
      !customOfferConstraintsData ||
      !productOfferConstraintsData ? (
        <PageMessage
          type="loading"
          localization={{
            title: getLocalizedText('offer-page.index.loadingTitle'),
          }}
        >
          <BodyText className="body__text">
            {getLocalizedText('offer-page.index.loadingBodyText')}
          </BodyText>
        </PageMessage>
      ) : (
        <OfferConfigurator
          offerConstraints={offerConstraints}
          averageMonthlyGrossSales={averageMonthlyGrossSales}
          applicationId={applicationId}
          customOfferConstraints={customOfferConstraintsData}
          productOfferConstraints={productOfferConstraintsData}
          applicationType={applicationType}
        />
      )}
    </>
  )
}

export default OfferPage
